import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Clock, ArrowRight, BookOpen } from 'lucide-react';

const LS_SHOWN_KEY = 'budget_period_ended_last_shown';
const TODAY = new Date().toISOString().slice(0, 10);

function formatCurrency(n) {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n ?? 0);
}

function getExpiredPeriodFromStorage() {
  try {
    const raw = localStorage.getItem('patrimony_budget_periods_v1');
    if (!raw) return null;
    const periods = JSON.parse(raw);
    const today = TODAY;
    return periods.find(p => p.end_date && p.end_date < today) ?? null;
  } catch {
    return null;
  }
}

function getTopCategories(period) {
  if (!period) return [];
  try {
    const snapshot = period.expenses_snapshot ?? [];
    const totals = {};
    snapshot.forEach(({ category, amount }) => {
      totals[category] = (totals[category] ?? 0) + Math.abs(amount ?? 0);
    });
    return Object.entries(totals)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([cat, amt]) => ({ cat, amt }));
  } catch {
    return [];
  }
}

// ── Main component ────────────────────────────────────────────────────────────

export default function BudgetPeriodEndedPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState(null);

  useEffect(() => {
    setPeriod(getExpiredPeriodFromStorage());
  }, []);

  const topCategories = getTopCategories(period);
  const totalSpent = period?.total_expenses ?? 0;

  const handleReadOnly = () => {
    localStorage.setItem(LS_SHOWN_KEY, TODAY);
    navigate('/budget');
  };

  const handleUpgrade = () => {
    localStorage.setItem(LS_SHOWN_KEY, TODAY);
    navigate('/pricing?source=budget_period_expired');
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-14 h-14 rounded-full bg-[#1a2b4a]/20 border border-[#1a2b4a]/40 flex items-center justify-center">
            <Clock className="w-7 h-7 text-[#c9a961]" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-center text-2xl font-bold text-white mb-2">
          Ta période budgétaire est terminée
        </h1>
        <p className="text-center text-zinc-400 text-sm mb-8 leading-relaxed">
          {period?.name
            ? `"${period.name}" est maintenant archivée en lecture seule.`
            : "Ta dernière période est archivée en lecture seule."}
          {" "}Continue ton pilotage financier avec Solo.
        </p>

        {/* Period summary card */}
        {period && (
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-6 space-y-4">
            <div className="flex items-center gap-2 text-xs text-zinc-500 uppercase tracking-widest">
              <BarChart2 className="w-3.5 h-3.5" />
              Résumé de la période
            </div>

            <div className="flex justify-between items-center">
              <span className="text-sm text-zinc-400">Total dépensé</span>
              <span className="text-lg font-bold text-white">{formatCurrency(totalSpent)}</span>
            </div>

            {topCategories.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-zinc-500">Top 3 catégories</p>
                {topCategories.map(({ cat, amt }) => (
                  <div key={cat} className="flex justify-between text-sm">
                    <span className="text-zinc-300 capitalize">{cat}</span>
                    <span className="text-zinc-400">{formatCurrency(amt)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CTAs — equal weight, no dark pattern */}
        <div className="flex flex-col gap-3">
          <button
            onClick={handleUpgrade}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
                       bg-[#1a2b4a] text-white font-semibold text-sm
                       hover:bg-[#243a63] transition-colors
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
          >
            Continuer avec Solo — 4,99 €/mois
            <ArrowRight className="w-4 h-4" />
          </button>

          <button
            onClick={handleReadOnly}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl
                       border-2 border-[#1a2b4a] text-[#c9a961] font-semibold text-sm
                       hover:bg-[#1a2b4a]/10 transition-colors
                       focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a2b4a]"
          >
            <BookOpen className="w-4 h-4" />
            Garder en lecture seule
          </button>
        </div>

        <p className="text-center text-xs text-zinc-600 mt-5">
          Tes données restent accessibles à vie, quelle que soit ta décision.
        </p>
      </div>
    </div>
  );
}

// ── Hook to trigger the page on app open ────────────────────────────────────

export function useBudgetPeriodEndedCheck(user) {
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;

    // BETA_MODE: every user has full Famille access regardless of plan,
    // so the period-ended nag (which redirects to /pricing) is muted.
    if (user.beta_mode) return;

    const plan = user.plan ?? 'gratuit';
    const isInTrial = (() => {
      const end = user.trial_end_date;
      if (!end) return false;
      return new Date(end) > new Date();
    })();

    // Only show for Gratuit users outside trial
    if (plan !== 'gratuit' || isInTrial) return;

    // Don't show twice the same calendar day
    const lastShown = localStorage.getItem(LS_SHOWN_KEY);
    if (lastShown === TODAY) return;

    // Check localStorage for an expired personal budget period
    const expired = getExpiredPeriodFromStorage();
    if (expired) {
      navigate('/budget-period-ended');
    }
  }, [user, navigate]);
}
